import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { artistApi } from '../services/artistApi';
import { notificationApi, Notification } from '../services/notificationApi';
import { useNotificationWebSocket } from '../hooks/useNotificationWebSocket';
import { Artist } from '../types/artist';
import './Header.css';

interface HeaderProps {
  activeTab: 'my-tracks' | 'upload' | 'give-feedback' | 'my-reviews' | 'account';
  onTabChange: (tab: 'my-tracks' | 'upload' | 'give-feedback' | 'my-reviews' | 'account') => void;
  isAdmin?: boolean;
  showAdminDashboard?: boolean;
  onAdminToggle?: () => void;
  showRequestFeedbackButton?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  activeTab,
  onTabChange,
  isAdmin = false,
  showAdminDashboard = false,
  onAdminToggle,
  showRequestFeedbackButton = false
}) => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [artistData, setArtistData] = useState<Artist | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState<number>(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const processedNotificationIds = useRef<Set<string>>(new Set());

  // WebSocket connection for real-time notifications
  const { latestNotification } = useNotificationWebSocket(isAuthenticated);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  // Close account menu dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setShowAccountMenu(false);
      }
    };

    if (showAccountMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAccountMenu]);

  // Fetch user's artist data when authenticated
  useEffect(() => {
    const fetchArtistData = async () => {
      if (isAuthenticated && user?.id) {
        try {
          const artists = await artistApi.getArtistsByUserId(user.id);
          if (artists.length > 0) {
            setArtistData(artists[0]); // Use the first artist
          }
        } catch (error) {
          console.error('Failed to fetch artist data:', error);
        }
      } else {
        setArtistData(null);
      }
    };

    fetchArtistData();
  }, [isAuthenticated, user?.id]);

  // Fetch initial unacknowledged notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      if (isAuthenticated) {
        try {
          const count = await notificationApi.getUnacknowledgedCount();
          setUnacknowledgedCount(count);
        } catch (error) {
          console.error('Failed to fetch notification count:', error);
        }
      } else {
        setUnacknowledgedCount(0);
      }
    };

    fetchNotificationCount();
  }, [isAuthenticated]);

  // Handle incoming WebSocket notifications in real-time
  useEffect(() => {
    if (latestNotification) {
      console.log('New notification received via WebSocket:', latestNotification);
      
      // Check if we've already processed this notification
      if (processedNotificationIds.current.has(latestNotification.id)) {
        console.log('Notification already processed, skipping:', latestNotification.id);
        return;
      }
      
      // Mark this notification as processed
      processedNotificationIds.current.add(latestNotification.id);
      
      // Increment unacknowledged count if the notification is not acknowledged
      if (!latestNotification.acknowledged) {
        setUnacknowledgedCount(prev => prev + 1);
      }

      // If the dropdown is open, prepend the new notification to the list
      if (showNotifications) {
        setNotifications(prev => [latestNotification, ...prev]);
      }
    }
  }, [latestNotification, showNotifications]);

  // Fetch notifications when dropdown is opened
  const handleNotificationClick = async () => {
    if (!showNotifications) {
      setShowNotifications(true);
      setLoadingNotifications(true);
      try {
        const result = await notificationApi.getNotifications(undefined, 1, 20);
        setNotifications(result.items);
        
        // Refresh the unacknowledged count to ensure accuracy
        const count = await notificationApi.getUnacknowledgedCount();
        setUnacknowledgedCount(count);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        setLoadingNotifications(false);
      }
    } else {
      setShowNotifications(false);
    }
  };

  // Handle clicking on a notification
  const handleNotificationItemClick = async (notification: Notification) => {
    try {
      // Acknowledge the notification
      await notificationApi.acknowledgeNotification(notification.id);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, acknowledged: true } : n)
      );
      setUnacknowledgedCount(prev => Math.max(0, prev - 1));

      // Close the dropdown
      setShowNotifications(false);

      // Navigate to action URL if present using React Router
      if (notification.actionUrl) {
        // Only strip trailing slash if there are no query parameters
        const hasQueryParams = notification.actionUrl.includes('?');
        const cleanUrl = hasQueryParams 
          ? notification.actionUrl 
          : (notification.actionUrl.replace(/\/+$/, '') || '/');
        
        // Get the path portion (before query params) for comparison
        const targetPath = cleanUrl.split('?')[0];
        const currentPath = location.pathname;
        const isSamePath = targetPath === currentPath;
        
        // Check if it's an internal route (starts with /)
        if (cleanUrl.startsWith('/')) {
          if (isSamePath) {
            // If on same path, navigate away and back to force refresh
            navigate('/');
            setTimeout(() => {
              navigate(cleanUrl, { replace: true });
            }, 0);
          } else {
            navigate(cleanUrl);
          }
        } else if (cleanUrl.startsWith('http')) {
          // External URL - use window.location for external links
          window.location.href = cleanUrl;
        } else {
          // Relative path - treat as internal route
          const fullPath = `/${cleanUrl}`;
          if (fullPath === currentPath) {
            navigate('/');
            setTimeout(() => {
              navigate(fullPath, { replace: true });
            }, 0);
          } else {
            navigate(fullPath);
          }
        }
      }
    } catch (error) {
      console.error('Failed to acknowledge notification:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.acknowledgeAllNotifications();
      setNotifications(prev => prev.map(n => ({ ...n, acknowledged: true })));
      
      // Refresh the count from server to ensure accuracy
      const count = await notificationApi.getUnacknowledgedCount();
      setUnacknowledgedCount(count);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <header className="app-header">
      <div className="header-content">
        {/* Top row on mobile: Logo and account actions */}
        <div className="header-top-row">
          {/* Logo on the left */}
          <div className="header-logo">
            <img 
              src="/music-feedback-logo-white.png" 
              alt="Unreleased Tracks Logo" 
              className="logo-image"
            />
          </div>

          {/* Right side - Account button and actions */}
          <div className="header-nav-right">
            {isAuthenticated && user && (
              <>
                {isAdmin && onAdminToggle && (
                  <button 
                    onClick={onAdminToggle}
                    className={`admin-toggle-button ${showAdminDashboard ? 'active' : ''}`}
                  >
                    {showAdminDashboard ? 'Exit Admin' : 'Admin'}
                  </button>
                )}
                
                {/* Notification Bell */}
                <div className="notification-container" ref={notificationRef}>
                  <button
                    className="notification-button"
                    onClick={handleNotificationClick}
                    aria-label="Notifications"
                  >
                    <svg 
                      className="notification-icon" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path 
                        d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                      <path 
                        d="M13.73 21a2 2 0 0 1-3.46 0" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                    {unacknowledgedCount > 0 && (
                      <span className="notification-badge">{unacknowledgedCount}</span>
                    )}
                  </button>

                  {showNotifications && (
                    <div className="notification-dropdown">
                      <div className="notification-header">
                        <h3>Notifications</h3>
                        {unacknowledgedCount > 0 && (
                          <button 
                            className="mark-all-read-button"
                            onClick={handleMarkAllAsRead}
                          >
                            Mark all as read
                          </button>
                        )}
                      </div>

                      <div className="notification-list">
                        {loadingNotifications ? (
                          <div className="notification-loading">Loading...</div>
                        ) : notifications.length === 0 ? (
                          <div className="notification-empty">No notifications</div>
                        ) : (
                          notifications.map(notification => (
                            <div
                              key={notification.id}
                              className={`notification-item ${!notification.acknowledged ? 'unread' : ''}`}
                              onClick={() => handleNotificationItemClick(notification)}
                            >
                              <div className="notification-content">
                                <div className="notification-type">{notification.type}</div>
                                <div className="notification-message">{notification.message}</div>
                                <div className="notification-time">{formatTimestamp(notification.timestamp)}</div>
                              </div>
                              {!notification.acknowledged && (
                                <div className="notification-unread-dot"></div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="account-menu-container" ref={accountMenuRef}>
                  <button
                    className={`nav-button account-button ${activeTab === 'account' ? 'active' : ''}`}
                    onClick={() => setShowAccountMenu(!showAccountMenu)}
                  >
                    {artistData?.profileImageUrl ? (
                      <img 
                        src={artistData.profileImageUrl} 
                        alt={artistData.name}
                        className="account-profile-image"
                      />
                    ) : (
                      <span className="account-icon">👤</span>
                    )}
                    <span className="account-name">{user.firstName}</span>
                    <svg 
                      className="account-dropdown-icon" 
                      width="12" 
                      height="12" 
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path 
                        d="M3 4.5L6 7.5L9 4.5" 
                        stroke="currentColor" 
                        strokeWidth="1.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {showAccountMenu && (
                    <div className="account-dropdown">
                      <button
                        className="account-dropdown-item"
                        onClick={() => {
                          setShowAccountMenu(false);
                          onTabChange('account');
                        }}
                      >
                        <svg 
                          className="dropdown-item-icon" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <circle 
                            cx="12" 
                            cy="7" 
                            r="4" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                        Account
                      </button>
                      <button
                        className="account-dropdown-item"
                        onClick={() => {
                          setShowAccountMenu(false);
                          navigate('/subscriptions');
                        }}
                      >
                        <svg 
                          className="dropdown-item-icon" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect 
                            x="3" 
                            y="3" 
                            width="18" 
                            height="18" 
                            rx="2" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <path 
                            d="M3 9h18" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <path 
                            d="M9 3v6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                        Subscriptions
                      </button>
                      <button
                        className="account-dropdown-item"
                        onClick={() => {
                          setShowAccountMenu(false);
                          navigate('/how-it-works');
                        }}
                      >
                        <svg 
                          className="dropdown-item-icon" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle 
                            cx="12" 
                            cy="12" 
                            r="10" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <path 
                            d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <line 
                            x1="12" 
                            y1="17" 
                            x2="12.01" 
                            y2="17" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                        How It Works
                      </button>
                      <button
                        className="account-dropdown-item logout-item"
                        onClick={() => {
                          setShowAccountMenu(false);
                          logout();
                        }}
                      >
                        <svg 
                          className="dropdown-item-icon" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path 
                            d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <polyline 
                            points="16 17 21 12 16 7" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                          <line 
                            x1="21" 
                            y1="12" 
                            x2="9" 
                            y2="12" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            {!isAuthenticated && (
              <button
                className={`nav-button account-button ${activeTab === 'account' ? 'active' : ''}`}
                onClick={() => onTabChange('account')}
              >
                Log In
              </button>
            )}
          </div>
        </div>

        {/* Bottom row on mobile: Navigation buttons */}
        <nav className="header-nav-center">
          <button
            className={`nav-button ${activeTab === 'my-tracks' ? 'active' : ''}`}
            onClick={() => onTabChange('my-tracks')}
          >
            My Tracks
          </button>
          <button
            className={`nav-button ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => onTabChange('upload')}
          >
            Upload
          </button>
          <button
            className={`nav-button ${activeTab === 'give-feedback' ? 'active' : ''}`}
            onClick={() => onTabChange('give-feedback')}
          >
            Give Feedback
          </button>
          <button
            className={`nav-button ${activeTab === 'my-reviews' ? 'active' : ''}`}
            onClick={() => onTabChange('my-reviews')}
          >
            My Reviews
          </button>
          {showRequestFeedbackButton && (
            <button className="nav-button active">
              Requested Feedback
            </button>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
